import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { HardDrives, Cloud, Database, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const REGIONS = [
    { value: "eu-central-2", label: "EU Central 2 (Amsterdam)", endpoint: "https://s3.eu-central-2.wasabisys.com" },
    { value: "eu-central-1", label: "EU Central 1 (Frankfurt)", endpoint: "https://s3.eu-central-1.wasabisys.com" },
    { value: "eu-west-1", label: "EU West 1 (London)", endpoint: "https://s3.eu-west-1.wasabisys.com" },
    { value: "eu-west-2", label: "EU West 2 (Paris)", endpoint: "https://s3.eu-west-2.wasabisys.com" },
    { value: "eu-south-1", label: "EU South 1 (Milan)", endpoint: "https://s3.eu-south-1.wasabisys.com" },
    { value: "us-east-1", label: "US East 1 (Virginia)", endpoint: "https://s3.us-east-1.wasabisys.com" },
    { value: "us-east-2", label: "US East 2 (Virginia)", endpoint: "https://s3.us-east-2.wasabisys.com" },
    { value: "us-central-1", label: "US Central 1 (Dallas)", endpoint: "https://s3.us-central-1.wasabisys.com" },
    { value: "us-west-1", label: "US West 1 (Oregon)", endpoint: "https://s3.us-west-1.wasabisys.com" },
    { value: "ap-northeast-1", label: "AP Northeast 1 (Tokyo)", endpoint: "https://s3.ap-northeast-1.wasabisys.com" },
];

export default function StorageSettingsCard() {
    const { data, mutate } = useSWR("/settings/storage", fetcher);
    const { data: stats } = useSWR("/settings/storage/stats", fetcher);

    const [provider, setProvider] = useState(null);
    const [region, setRegion] = useState("");
    const [endpoint, setEndpoint] = useState("");
    const [bucket, setBucket] = useState("");
    const [accessKey, setAccessKey] = useState("");
    const [secret, setSecret] = useState("");
    const [showSecret, setShowSecret] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    // Hydrate state from API once
    const activeProvider = provider ?? data?.provider ?? "gridfs";
    const isWasabi = activeProvider === "wasabi";

    const effectiveRegion = region || data?.wasabi_region || "";
    const effectiveEndpoint = endpoint || data?.wasabi_endpoint || "";
    const effectiveBucket = bucket || data?.wasabi_bucket || "";
    const effectiveAccess = accessKey || data?.wasabi_access_key || "";

    const buildPayload = () => ({
        provider: activeProvider,
        wasabi_region: effectiveRegion || null,
        wasabi_endpoint: effectiveEndpoint || null,
        wasabi_bucket: effectiveBucket || null,
        wasabi_access_key: effectiveAccess || null,
        wasabi_secret: secret || null,
    });

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings/storage", buildPayload());
            toast.success("Stockage configuré · les nouveaux PDFs iront vers " + (isWasabi ? "Wasabi" : "MongoDB GridFS"));
            setSecret("");
            mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        try {
            const { data: r } = await api.post("/settings/storage/test", buildPayload());
            toast.success(r.message || "Connexion Wasabi OK");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test échoué");
        } finally {
            setTesting(false);
        }
    };

    const fmtSize = (bytes) => {
        if (!bytes) return "0 MB";
        const mb = bytes / 1024 / 1024;
        if (mb < 1024) return `${mb.toFixed(1)} MB`;
        return `${(mb / 1024).toFixed(2)} GB`;
    };

    return (
        <Card className="ec-card" data-testid="storage-settings-card">
            <CardContent className="p-6 space-y-5">
                <div className="flex items-start gap-3">
                    <HardDrives size={22} className="text-primary mt-0.5" />
                    <div className="flex-1">
                        <h2 className="font-display text-lg font-bold tracking-tight">
                            Stockage des PDFs
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Choisissez où sont stockés les PDFs (mails, scanner, Amazon…). La bascule n&apos;affecte que les <strong>nouveaux</strong> documents ; les anciens restent accessibles depuis leur stockage d&apos;origine.
                        </p>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="rounded border border-border p-2.5">
                            <div className="overline">MongoDB total</div>
                            <div className="font-display font-bold text-base mt-0.5">{fmtSize(stats.mongo_total_bytes)}</div>
                        </div>
                        <div className="rounded border border-border p-2.5">
                            <div className="overline">PDFs dans GridFS</div>
                            <div className="font-display font-bold text-base mt-0.5">
                                {stats.user_gridfs_pdfs} <span className="text-muted-foreground font-normal text-xs">({fmtSize(stats.gridfs_bytes)})</span>
                            </div>
                        </div>
                        <div className="rounded border border-border p-2.5">
                            <div className="overline">PDFs sur Wasabi</div>
                            <div className="font-display font-bold text-base mt-0.5">{stats.wasabi_pdf_count}</div>
                        </div>
                    </div>
                )}

                {/* Provider radio cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={() => setProvider("gridfs")}
                        className={`text-left rounded-md border-2 p-4 transition ${activeProvider === "gridfs" ? "border-primary bg-accent/30" : "border-border hover:border-primary/50"}`}
                        data-testid="provider-gridfs"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Database size={18} className="text-primary" />
                            <span className="font-display font-bold">MongoDB GridFS</span>
                            {activeProvider === "gridfs" && <CheckCircle size={16} weight="fill" className="text-primary ml-auto" />}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Stockage intégré dans la base. Recommandé jusqu&apos;à ~1 000 PDFs. Aucun setup requis.
                        </p>
                    </button>
                    <button
                        type="button"
                        onClick={() => setProvider("wasabi")}
                        className={`text-left rounded-md border-2 p-4 transition ${activeProvider === "wasabi" ? "border-primary bg-accent/30" : "border-border hover:border-primary/50"}`}
                        data-testid="provider-wasabi"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Cloud size={18} className="text-primary" />
                            <span className="font-display font-bold">Wasabi S3</span>
                            {activeProvider === "wasabi" && <CheckCircle size={16} weight="fill" className="text-primary ml-auto" />}
                            {data?.wasabi_secret_set && (
                                <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 ml-1">
                                    Configuré
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Stockage objet illimité, ~7 $/TB/mois, pas de frais de sortie. Pour les usages à plus de 1 000 PDFs.
                        </p>
                    </button>
                </div>

                {/* Wasabi config */}
                {isWasabi && (
                    <div className="space-y-4 pt-2 border-t border-border">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="wasabi-region">Région</Label>
                                <select
                                    id="wasabi-region"
                                    value={effectiveRegion}
                                    onChange={(e) => {
                                        setRegion(e.target.value);
                                        const r = REGIONS.find((x) => x.value === e.target.value);
                                        if (r) setEndpoint(r.endpoint);
                                    }}
                                    className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                                    data-testid="wasabi-region"
                                >
                                    <option value="">— Choisir une région —</option>
                                    {REGIONS.map((r) => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="wasabi-bucket">Nom du bucket</Label>
                                <Input
                                    id="wasabi-bucket"
                                    value={effectiveBucket}
                                    onChange={(e) => setBucket(e.target.value)}
                                    placeholder="scanbox-pdfs-prod"
                                    data-testid="wasabi-bucket"
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="wasabi-endpoint">
                                Endpoint URL
                                <span className="ml-2 text-xs text-muted-foreground font-normal">(rempli automatiquement selon la région)</span>
                            </Label>
                            <Input
                                id="wasabi-endpoint"
                                value={effectiveEndpoint}
                                onChange={(e) => setEndpoint(e.target.value)}
                                placeholder="https://s3.eu-central-2.wasabisys.com"
                                data-testid="wasabi-endpoint"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="wasabi-access">Access Key ID</Label>
                                <Input
                                    id="wasabi-access"
                                    value={effectiveAccess}
                                    onChange={(e) => setAccessKey(e.target.value)}
                                    placeholder="A0XXXXXXXXXXXXXXXXXX"
                                    data-testid="wasabi-access"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="wasabi-secret">
                                    Secret Access Key
                                    {data?.wasabi_secret_set && (
                                        <span className="ml-2 text-xs text-emerald-700 font-normal">
                                            (déjà enregistré — laisser vide pour conserver)
                                        </span>
                                    )}
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="wasabi-secret"
                                        type={showSecret ? "text" : "password"}
                                        value={secret}
                                        onChange={(e) => setSecret(e.target.value)}
                                        placeholder={data?.wasabi_secret_set ? "•••••••••••••••" : "Saisir le secret"}
                                        data-testid="wasabi-secret"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowSecret(!showSecret)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showSecret ? <EyeSlash size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                            ⚠️ <strong>Rétention 90 jours minimum chez Wasabi :</strong> tout PDF stocké est facturé au moins 90 jours, même supprimé immédiatement.
                            Pensez à créer un bucket dédié à ScanBox pour faciliter la gestion.
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 pt-2 border-t border-border">
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="storage-save"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </Button>
                    {isWasabi && (
                        <Button
                            onClick={test}
                            disabled={testing}
                            variant="outline"
                            data-testid="storage-test"
                        >
                            {testing ? "Test en cours…" : "Tester la connexion Wasabi"}
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
